import React from "react";
import { style } from "typestyle";
import Select, { components, StylesConfig } from "react-select";
import type { SrsName, SrsPair } from "../types";
import without from "lodash/without";
import Grid from "./Grid";

type SrsOption = { label: string, value: SrsName };
type SrsSet = SrsName[];
type SrsSelectorProps = {
  srsSet: SrsSet,
  srsPair: SrsPair,
  onChange: (srsPair: SrsPair) => void,
};

const srsOptionGlobal: SrsOption = {
  label: "Global",
  value: "global",
};

const srsOptionAssumed: SrsOption = {
  label: "Assumed",
  value: "assumed",
};

const srsOptionWgs84: SrsOption = {
  label: "WGS84",
  value: "wgs84",
};

export default function SrsSelector(props: SrsSelectorProps) {
  const srsOptionsSet: SrsOption[] = [
    srsOptionAssumed,
    srsOptionGlobal,
    srsOptionWgs84,
  ].filter(({ value }) => {
    return props.srsSet.indexOf(value) > -1;
  });

  function findSrsOption(srsId: string, srsOptionsSet: SrsOption[]) {
    return srsOptionsSet.find(({ value }: SrsOption) => srsId === value);
  }

  function updateSrsPair(newSrs: SrsName, srsPair: SrsPair, srsSet: SrsSet): SrsPair {
    const fromSrs = newSrs;
    if (srsPair[1] !== newSrs) {
      return [fromSrs, srsPair[1]];
    }

    return [fromSrs, without(srsSet, newSrs)[0]];
  }

  return (
    <Grid
      left={
        <Select
          styles={customSelectStyles}
          components={{ DropdownIndicator }}
          options={srsOptionsSet}
          onChange={({ value }) => {
            props.onChange(updateSrsPair(value, props.srsPair, props.srsSet));
          }}
          value={findSrsOption(props.srsPair[0], srsOptionsSet)}
        />
      }
      center={
        <svg
          className={style({ minWidth: "24px" })}
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 8.00002V6.41002C12 5.52002 13.08 5.07002 13.71 5.70002L19.3 11.29C19.69 11.68 19.69 12.31 19.3 12.7L13.71 18.29C13.08 18.92 12 18.48 12 17.59V16H5C4.45 16 4 15.55 4 15V9.00002C4 8.45002 4.45 8.00002 5 8.00002H12Z"
            fill="black"
          />
        </svg>
      }
      right={
        <Select
          styles={customSelectStyles}
          components={{ DropdownIndicator }}
          options={srsOptionsSet}
          onChange={({ value }) => {
            const [fromSrsSrc, toSrsSrc] = props.srsPair;
            const [fromSrsDst, toSrsDst] = updateSrsPair(
              value,
              [toSrsSrc, fromSrsSrc],
              props.srsSet,
            );
            props.onChange([toSrsDst, fromSrsDst]);
          }}
          value={findSrsOption(props.srsPair[1], srsOptionsSet)}
        />
      }
    />
  );
}

const customSelectStyles: StylesConfig<SrsOption, false> = {
  control: provided => ({
    ...provided,
    borderRadius: "12px",
    height: "56px",
  }),
  indicatorSeparator: provided => ({
    ...provided,
    display: "none",
  }),
  valueContainer: provided => ({
    ...provided,
    padding: "0px 24px",
    fontSize: "20px",
    lineHeight: "28px",
  }),
  dropdownIndicator: () => ({
    padding: "0 25px",
  }),
};

const DropdownIndicator = (
  props: React.ComponentProps<typeof components.DropdownIndicator>,
) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg
        width="12"
        height="7"
        viewBox="0 0 12 7"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.540601 0.333313C0.0951489 0.333313 -0.127933 0.871884 0.187049 1.18687L5.46983 6.46965C5.76273 6.76254 6.2376 6.76254 6.53049 6.46965L11.8133 1.18687C12.1283 0.871883 11.9052 0.333313 11.4597 0.333313H0.540601Z"
          fill="#64646E"
        />
      </svg>
    </components.DropdownIndicator>
  );
};
